import { HttpClient } from '@angular/common/http';
import {publishReplay, map, take} from 'rxjs/operators';
import {BehaviorSubject, ConnectableObservable} from 'rxjs';
import {DjangoPage} from '../../django-paginator';
import {ComponentFactoryResolver, Injectable, ApplicationRef, Injector, EmbeddedViewRef} from '@angular/core';

interface Event {
  city: string;
  id: number;
  name: string;
  thumbnail: string;
}

interface EventGroup {
  id: number;
  name: string;
  thumbnail: string;
}

interface WinGame {
  id: number;
  name: string;
  title: string;
  thumbnail: string;
}

interface Product {
  id: number;
  name: string;
  title: string;
  photo: string;
}

interface Profile {
  id: number;
  name: string;
  thumbnail: string;
}

interface Page {
  id: string;
  name: string;
  thumbnail: string;
}

interface MailingList {
  id: number;
  name: string;
}

interface CustomAudienceGroup {
  id: number;
  name: string;
}

interface Estimation {
  count: number;
  include: number;
  exclude: number;
}

export interface UploadedFile {
  name: string;
  count: number;
  src: string;
  id: number;
  error?: string;
}

@Injectable({providedIn: 'root'})
export class TargetAudienceService {
  estimatedSize = new BehaviorSubject({
    count: 0,
    include: 0,
    exclude: 0,
  });

  private componentRef: any;

  constructor(
    private http: HttpClient,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
  ) {}

  fetchEvents(e?) {
    const obs$ = this.http
      .get(`rest-api/v1/events/search?q=${encodeURIComponent(e ? e : '')}`)
      .pipe(publishReplay()) as ConnectableObservable<[Event]>;
    obs$.connect();
    return obs$;
  }

  fetchWingames(e?) {
    const obs$ = this.http
      .get<DjangoPage<WinGame>>(`rest-api/admin-panel/api/v1/admin/win-games?companyId=0&name=${e ? e : ''}`)
      .pipe(
        map(res => res.results),
        publishReplay(),
      ) as ConnectableObservable<[WinGame]>;
    obs$.connect();
    return obs$;
  }

  fetchProducts(e?, params?): ConnectableObservable<Product[]> {
    const obs$ = this.http
      .get<
        DjangoPage<Product>
      >(`rest-api/v1/admin/companies/0/shop/products?name=${e ? e : ''}${params ? '&' + params : ''}`)
      .pipe(
        map(res => res.results),
        publishReplay(),
      ) as ConnectableObservable<Product[]>;
    obs$.connect();
    return obs$;
  }

  fetchEventGroups(e?) {
    const obs$ = this.http
      .get(`rest-api/v1/event_groups/search?q=${e ? e : ''}`)
      .pipe(publishReplay()) as ConnectableObservable<[EventGroup]>;
    obs$.connect();
    return obs$;
  }

  fetchEventGroupsObj = (params) => {
    return this.http.get(`rest-api/v1/event_groups/search`, { params });
  };

  fetchProfiles(e?) {
    const obs$ = this.http
      .get(`rest-api/v1/profiles/search?companyId=0&q=${e ? e : ''}`)
      .pipe(publishReplay()) as ConnectableObservable<[Profile]>;
    obs$.connect();
    return obs$;
  }

  fetchPages(pageType, e?) {
    const obs$ = this.http
      .get<DjangoPage<Page>>(`/rest-api/v1/community/search/pages/${pageType}?companyId=0&name=${e ? e : ''}`)
      .pipe(
        map(res => res.results),
        publishReplay(),
      ) as ConnectableObservable<[Page]>;
    obs$.connect();
    return obs$;
  }

  updateEstimatedSize(body) {
    this.http
      .post<Estimation>('rest-api/v1/admin/target-audiences/estimations', body)
      .pipe(take(3))
      .subscribe(res => {
        this.estimatedSize.next(res);
      });
  }

  getEstimatedSize() {
    return this.estimatedSize.asObservable();
  }

  fetchMailingLists(e?) {
    const obs$ = this.http.get<DjangoPage<Page>>(`rest-api/emails/mailing-lists?search=${e ? e : ''}`).pipe(
      map(res => res.results),
      publishReplay(),
    ) as ConnectableObservable<[MailingList]>;
    obs$.connect();
    return obs$;
  }

  fetchCustomAudienceGroup(e?) {
    const obs$ = this.http.get(`rest-api/v1/companies/0/custom-audiences/filter_custom_groups?name=${e ? e : ''}`).pipe(
      map((res: any[]) => {
        return res;
      }),
      publishReplay(),
    ) as ConnectableObservable<[CustomAudienceGroup]>;
    obs$.connect();
    return obs$;
  }

  uploadCsv(file_content) {
    const payload = new FormData();

    payload.append('thumbnail', file_content);
    const obs$ = this.http.post('rest-api/v1/admin/target-audiences/upload_csv', payload).pipe(
      map(res => res),
      publishReplay(),
    ) as ConnectableObservable<UploadedFile>;
    obs$.connect();
    return obs$;
  }

  deleteCsv = id => this.http.post('rest-api/v1/admin/target-audiences/delete_csv', {id: id});

  stringifiedAudience(categories) {
    // deep copy to preserve the original settings untouched
    const stringified = JSON.parse(JSON.stringify(categories));
    Object.keys(stringified).forEach(key => {
      Object.keys(stringified[key]).forEach(key2 => {
        if (typeof stringified[key][key2] === 'object') {
          const data = stringified[key][key2];
          const filtered = data.filter(n => Object.keys(n).length !== 0);
          if (filtered && filtered.length > 0) {
            stringified[key][key2] = filtered;
          } else {
            delete stringified[key][key2];
          }
        }
      });
    });
    return stringified;
  }

  undoStringifiedAudience(stringifiedData) {
    const originalData = JSON.parse(JSON.stringify(stringifiedData));
    Object.keys(originalData).forEach(key => {
      Object.keys(originalData[key]).forEach(key2 => {
        if (typeof originalData[key][key2] === 'string') {
          originalData[key][key2] = JSON.parse(originalData[key][key2]);
        }
      });
    });
    return originalData;
  }

  targetAudiencePredefinedEventObj(data) {
    const start = data.event.start.split(' ');
    const ob = {
      id: data.event_id,
      name: data.event.name + ' (' + data.event.start + ')',
      start: start[0],
      thumbnail: data.photo_url,
      venue_name: '',
      city: '',
      company: null,
    };

    return {
      included: {
        event_attending: [ob],
        event_following: [ob],
      },
      excluded: {},
    };
  }

  targetAudiencePredefinedEventObjFromProduct(data) {
    const start = data.available_from.split(' ');
    const ob = {
      id: data.relation.id,
      name: data.relation.name + ' (' + data.available_from + ')',
      start: start[0],
      thumbnail: data.relation.thumbnail,
      venue_name: '',
      city: '',
      company: null,
    };

    return {
      included: {
        event_attending: [ob],
        event_following: [ob],
      },
      excluded: {},
    };
  }
}
